import React, { useState } from 'react';
import {
  Wrapper,
  EllipsisSpan,
  useUpdate,
  useBreadcrumbRoutes,
  Button,
  DatePicker,
  Form,
  Tag,
  Input,
  Select,
  CustomFilter,
  Paging,
  usePaging,
  Table,
  message,
  Modal,
} from '@maxtropy/components';
import { Space, Upload as AntdUpload, Tooltip } from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs, { Dayjs } from 'dayjs';
import { useRequest } from 'ahooks';
import styles from './index.module.scss';
import DetailModal from './components/DetailModal';
import {
  apiV2DeletePost,
  apiV2PowerOutageAlarmPageV2Post,
  V2PowerOutageAlarmPageV2PostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { AlarmState, AlarmStateColorDisplay, AlarmStateDisplay } from '@/shared/types';
import { omit } from 'lodash-es';
import { usePowerOutageAlarmRefresh } from './components/hooks';

// 数据类型定义
export type PowerOutageAlarmItem = Exclude<V2PowerOutageAlarmPageV2PostResponse['list'], undefined>[number];

// 筛选参数类型
export interface FilterParams {
  powerOutageTime?: [Dayjs, Dayjs]; // 停电日期范围
  affectedArea?: string; // 影响区域
  affectedDevice?: string; // 影响设备
  powerOutageStation?: string; // 停电站房
  state?: number; // 报警状态（空字符串表示全部）
}

// 搜索参数类型（转换为接口参数格式）
export interface SearchParams {
  startDate?: number;
  endDate?: number;
  affectedArea?: string;
  affectedDevice?: string;
  powerOutageStation?: string;
  state?: number;
}

const dateFormat = 'YYYY-MM-DD';

// 报警状态选项
const alarmStateOptions = [
  { label: '全部', value: 2 }, // 2 代表全部，前端特殊处理
  { label: '已恢复', value: AlarmState.RECOVER },
  { label: '未恢复', value: AlarmState.UNRECOVERED },
];

// 处理文本溢出显示（两行展示，超出省略号）
const renderTextWithTooltip = (text: string | undefined) => {
  if (!text) return '--';
  return (
    <Tooltip title={text}>
      <span className={styles.twoLineText}>{text}</span>
    </Tooltip>
  );
};

const baseColumns = [
  {
    title: '停电时间',
    dataIndex: 'powerOutageTime',
    ellipsis: true,
    width: 140,
    render: (v: number) => <EllipsisSpan value={v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '--'} />,
  },
  {
    title: '报警状态',
    dataIndex: 'state',
    ellipsis: true,
    width: 100,
    render: (v: number) => {
      const state = v as AlarmState;
      return (
        <Tag border="solid" color={AlarmStateColorDisplay[state] || 'default'} style={{ width: 'fit-content' }}>
          {AlarmStateDisplay[state] || '--'}
        </Tag>
      );
    },
  },
  {
    title: '影响区域',
    dataIndex: 'affectedArea',
    width: 200,
    render: (v: string) => renderTextWithTooltip(v),
  },
  {
    title: '报警条数',
    dataIndex: 'alarmCount',
    width: 100,
    render: (v: number) => <EllipsisSpan value={v} />,
  },
  {
    title: '影响设备',
    dataIndex: 'affectedDevices',
    width: 200,
    render: (v: string) => renderTextWithTooltip(v),
  },
  {
    title: '停电站房',
    dataIndex: 'powerOutageStation',
    width: 200,
    render: (v: string) => renderTextWithTooltip(v),
  },
];

const PowerOutageAlarmAnalysis: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [form] = Form.useForm();
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [updateState, updateFn] = useUpdate(); // 用于触发刷新

  const [searchParams, setSearchParams] = useState<SearchParams>();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<PowerOutageAlarmItem>();
  const pushRefreshTrigger = usePowerOutageAlarmRefresh();

  // 批量删除相关状态
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // 定时刷新引用
  // const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  // const lastUserActivityRef = useRef<number>(Date.now());

  // 获取表格数据
  const { data: tableData, loading: tableLoading } = useRequest(
    async () => {
      const res = await apiV2PowerOutageAlarmPageV2Post({
        ...searchParams,
        page: pageOffset,
        size: pageSize,
      });
      setTotalCount(res?.total || 0);
      return res?.list || [];
    },
    {
      refreshDeps: [pageOffset, pageSize, searchParams, updateState, pushRefreshTrigger],
    }
  );

  // 查看详情
  const handleViewDetail = (record: PowerOutageAlarmItem) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  // 关闭详情弹窗
  const handleCloseDetailModal = () => {
    setDetailModalVisible(false);
    setSelectedRecord(undefined);
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的数据');
      return;
    }
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 条数据吗？删除后不可恢复。`,
      onOk: async () => {
        try {
          await apiV2DeletePost({
            ids: selectedRowKeys.map(id => Number(id)),
          });
          message.success('删除成功');
          setSelectedRowKeys([]);
          updateFn();
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  // 导入前校验
  const beforeUpload = (file: File) => {
    const fileName = file.name.toLowerCase();
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    if (!isExcel) {
      Modal.warning({
        content: '文件格式错误！仅支持 .xlsx、.xls',
      });
      return AntdUpload.LIST_IGNORE;
    }
    return true;
  };

  // 导入状态变化
  const handleImportChange = (info: UploadChangeParam<UploadFile>) => {
    const { file } = info;
    if (file.status === 'done') {
      if (file.response.code === 0) {
        message.success('导入成功');
        updateFn();
        return;
      }
      const errorMsg = file.response?.msg || '导入失败';

      message.error(errorMsg);
    } else if (file.status === 'error') {
      const errorMsg = file.response?.msg || '导入失败';
      message.error(errorMsg);
    }
  };

  // 下载已导入的配置
  const handleExport = () => {
    window.open('/api/v2/powerOutageAlarm/export', '_blank');
  };

  // 表格列配置
  const columns = [
    ...baseColumns,
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right' as 'right',
      render: (_: unknown, record: PowerOutageAlarmItem) => (
        <Space size="small">
          <Button type="link" onClick={() => handleViewDetail(record)}>
            查看
          </Button>
        </Space>
      ),
    },
  ];

  const onFinish = (values: FilterParams) => {
    setPageOffset(1);
    const params: SearchParams = omit(values, ['powerOutageTime', 'state']) as SearchParams;

    if (values.powerOutageTime) {
      params.startDate = values.powerOutageTime[0].startOf('date').valueOf();
      params.endDate = values.powerOutageTime[1].endOf('date').valueOf();
    }

    // 只有选择了已恢复或未恢复时才传state参数
    if (values.state !== 2) {
      params.state = values.state;
    }

    setSearchParams(params);
  };

  // 筛选重置
  const onReset = () => {
    setPageOffset(1);
    setSearchParams({});
  };

  // 自动刷新（报警状态实时刷新）
  // useEffect(() => {
  //   // 30秒自动刷新
  //   refreshTimerRef.current = setInterval(() => {
  //     const now = Date.now();
  //     const inactivityThreshold = 5 * 60 * 1000; // 5 分钟无操作则停止刷新
  //     if (now - lastUserActivityRef.current < inactivityThreshold) {
  //       handleRefresh();
  //     }
  //   }, 30 * 1000);

  //   // 监听用户活动事件
  //   const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
  //   const recordUserActivity = () => {
  //     lastUserActivityRef.current = Date.now();
  //   };
  //   events.forEach(event => {
  //     document.addEventListener(event, recordUserActivity);
  //   });

  //   return () => {
  //     if (refreshTimerRef.current) {
  //       clearInterval(refreshTimerRef.current);
  //     }
  //     events.forEach(event => {
  //       document.removeEventListener(event, recordUserActivity);
  //     });
  //   };
  // }, [handleRefresh]);

  const filters = (
    <CustomFilter<FilterParams> form={form} onFinish={onFinish} onReset={onReset}>
      <Form.Item name="powerOutageTime" label="停电日期">
        <DatePicker.RangePicker style={{ width: '100%' }} format={dateFormat} placeholder={['开始日期', '结束日期']} />
      </Form.Item>
      <Form.Item name="affectedArea" label="影响区域">
        <Input placeholder="请输入" allowClear />
      </Form.Item>
      <Form.Item name="affectedDevice" label="影响设备">
        <Input placeholder="请输入" allowClear />
      </Form.Item>
      <Form.Item name="powerOutageStation" label="停电站房">
        <Input placeholder="请输入" allowClear />
      </Form.Item>
      <Form.Item name="state" label="报警状态">
        <Select style={{ width: '100%' }} defaultValue={2} placeholder="请选择" options={alarmStateOptions} />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} filters={filters} className={styles.wrapper}>
      <Space size="small" style={{ marginBottom: 16 }}>
        <AntdUpload
          action="/api/v2/powerOutageAlarm/import"
          accept=".xlsx,.xls"
          maxCount={1}
          showUploadList={false}
          beforeUpload={beforeUpload}
          onChange={handleImportChange}
        >
          <Button type="primary">导入停电分析配置</Button>
        </AntdUpload>
        <Button onClick={handleExport}>下载已导入的配置</Button>
        <Button type="primary" onClick={handleBatchDelete}>
          批量删除
        </Button>
      </Space>
      <Table
        rowKey="id"
        sticky
        loading={tableLoading}
        columns={columns}
        dataSource={tableData}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: true,
        }}
      />
      <Paging pagingInfo={pagingInfo} />

      {detailModalVisible && (
        <DetailModal visible={detailModalVisible} record={selectedRecord} onClose={handleCloseDetailModal} />
      )}
    </Wrapper>
  );
};

export default PowerOutageAlarmAnalysis;
